// RESONATE_ANALYTICS_INSTRUMENTED
// RESONATE_ANALYTICS_WAVE2
import { useEffect, useRef } from "react";
import { trackSectionView } from "@/lib/analytics";

/**
 * Wraps a landing-page section. Two behaviours:
 *  1. Fires section_view ONCE when >=50% of the section enters viewport.
 *  2. Updates window.location.hash to the section id when it becomes the
 *     dominant section in view (>=50% visible), using history.replaceState so
 *     we don't pollute browser history. Gives users shareable deep links AND
 *     answers "why doesn't the URL change when I scroll" intuitively.
 */
export const TrackedSection = ({ id, name, children }) => {
  const ref = useRef(null);
  const viewFired = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;

    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            // Fire section_view once
            if (!viewFired.current) {
              viewFired.current = true;
              trackSectionView(id, name);
            }
            // Update URL hash (replaceState, never pushState - no history bloat)
            try {
              const newHash = id === "hero" ? "" : "#" + id;
              if (window.location.hash !== newHash) {
                const newUrl = window.location.pathname + window.location.search + newHash;
                window.history.replaceState(null, "", newUrl);
              }
            } catch (e) { /* ignore */ }
          }
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [id, name]);

  return <div ref={ref} id={id} data-section={id}>{children}</div>;
};
